<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { renderMarkdown } from '@/lib/markdown'
import { matrix } from '@/lib/matrix'
import { memoryApi } from '@/lib/memoryApi'
import { useEmoji } from '@/stores/emoji'

/* Rendered Markdown with live media: ![alt](mxc://…) placeholders come out of
   the renderer src-less (downloads are authenticated), and this component
   hydrates them with token-fetched blob URLs. Clicking an embedded image asks
   the parent to open a preview. With interactiveTasks, "- [ ]" checkboxes are
   clickable and emit their index so the parent can persist the toggle. */
/* `mentions` and `autolink` are opt-in chat niceties: the renderer only handles
   markdown links/images, so chat passes its @-names to highlight and turns on
   bare-URL linkifying. Both run as a post-render pass over TEXT NODES only —
   never touching tags, hrefs or code — so callers that omit them (tickets) are
   wholly unaffected. */
const props = defineProps<{ source: string; interactiveTasks?: boolean; mentions?: string[]; autolink?: boolean }>()
const emit = defineEmits<{ preview: [mxc: string]; toggleTask: [index: number, checked: boolean]; nodeLink: [id: string] }>()

const el = ref<HTMLElement | null>(null)
const html = ref('')
// Custom emotes (:name: → inline image) apply everywhere markdown renders.
const emoji = useEmoji()

watch(
  () => [props.source, props.interactiveTasks, props.autolink, props.mentions, emoji.emoteMap] as const,
  async () => {
    html.value = renderMarkdown(props.source, { interactiveTasks: props.interactiveTasks, meId: matrix.userId(), emotes: emoji.emoteMap })
    await nextTick()
    el.value?.querySelectorAll<HTMLImageElement>('img[data-mxc]:not([src])').forEach((img) => {
      const mxc = img.dataset.mxc
      if (mxc) void matrix.mediaObjectUrl(mxc).then((url) => { if (url) img.src = url })
    })
    el.value?.querySelectorAll<HTMLImageElement>('img[data-memfile]:not([src])').forEach((img) => {
      const id = Number(img.dataset.memfile)
      if (id) void memoryApi.fileObjectUrl(id).then((url) => { if (url) img.src = url })
    })
    if (el.value && props.autolink) linkifyBareUrls(el.value)
    if (el.value && props.mentions?.length) markMentions(el.value, props.mentions)
  },
  { immediate: true },
)

/** Walk text nodes (skipping links/code) and replace regex hits with elements. */
function replaceInText(root: HTMLElement, re: RegExp, make: (match: string) => Node) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const targets: Text[] = []
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const t = n as Text
    if (t.parentElement?.closest('a, code, pre, mark, .mention')) continue
    re.lastIndex = 0
    if (re.test(t.data)) targets.push(t)
  }
  for (const t of targets) {
    const frag = document.createDocumentFragment()
    const data = t.data
    let last = 0
    re.lastIndex = 0
    for (let m = re.exec(data); m; m = re.exec(data)) {
      if (m.index > last) frag.appendChild(document.createTextNode(data.slice(last, m.index)))
      frag.appendChild(make(m[0]))
      last = m.index + m[0].length
      if (m[0].length === 0) re.lastIndex++ // never spin on a zero-width match
    }
    if (last < data.length) frag.appendChild(document.createTextNode(data.slice(last)))
    t.replaceWith(frag)
  }
}

function linkifyBareUrls(root: HTMLElement) {
  replaceInText(root, /https?:\/\/[^\s<]+/g, (url) => {
    const a = document.createElement('a')
    a.className = 'chat-link'
    a.href = url
    const external = !url.startsWith(location.origin)
    if (external) { a.target = '_blank'; a.rel = 'noopener noreferrer' }
    a.textContent = url
    return a
  })
}

function markMentions(root: HTMLElement, names: string[]) {
  const re = new RegExp('@?(?:' + names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')(?![\\w])', 'gi')
  replaceInText(root, re, (txt) => {
    const mark = document.createElement('mark')
    mark.className = 'chat-mention'
    mark.textContent = txt
    return mark
  })
}

function onClick(ev: MouseEvent) {
  const t = ev.target as HTMLElement
  // [text](node-slug) memory-graph links: no href, the parent decides what
  // "open this node" means (select in the explorer, usually).
  const nodeLink = t.closest<HTMLElement>('a.md-node-link')
  if (nodeLink?.dataset.node) {
    emit('nodeLink', nodeLink.dataset.node)
    return
  }
  if (t.tagName === 'IMG' && (t as HTMLImageElement).dataset.mxc && !t.classList.contains('md-emote')) {
    emit('preview', (t as HTMLImageElement).dataset.mxc!)
    return
  }
  if (t.tagName === 'IMG' && (t as HTMLImageElement).dataset.memfile) {
    // Memory-layer files have no mxc preview path; the blob URL opens fine.
    const src = (t as HTMLImageElement).src
    if (src) window.open(src, '_blank', 'noopener')
    return
  }
  if (props.interactiveTasks && t.tagName === 'INPUT' && (t as HTMLInputElement).type === 'checkbox') {
    const box = t as HTMLInputElement
    const all = Array.from(el.value?.querySelectorAll('input[data-task]') ?? [])
    const index = all.indexOf(box)
    if (index >= 0) emit('toggleTask', index, box.checked)
  }
}
</script>

<template>
  <div ref="el" class="md" v-html="html" @click="onClick" />
</template>
